/**
 * Loading — the hub's one loading state (Sep 2026). Replaces the bare
 * "Loading…" lines: the hub mark (a core with three orbiting nodes for People,
 * Finance and Revenue, the same idea as the favicon) plus a short rotating line
 * so an 8-second cold start reads as "working", not "broken".
 *
 * The words only appear after a beat (fast loads never flash text) and the
 * motion stops under prefers-reduced-motion (styles/index.css).
 *
 *   <Loading />                   default block, centred in its container
 *   <Loading label="statements"/> first line names what is loading
 *   <Loading inline />            small, for a table cell or a button row
 */
import { useEffect, useState } from 'react';

const LINES = [
  'Waking up the servers',
  'Rounding up the receipts',
  'Counting vacation days',
  'Asking finance nicely',
  'Polishing the trophies',
  'Lining up the numbers',
  'Checking with the org chart',
  'Sorting the paperwork',
  'Brewing a fresh batch of data',
  'Almost there',
];

export function HubMark({ size = 40, spinning = true }: { size?: number; spinning?: boolean }) {
  return (
    <svg width={size} height={size} viewBox="0 0 48 48" aria-hidden="true" className="shrink-0">
      <circle cx="24" cy="24" r="17" fill="none" stroke="rgb(var(--line-strong))" strokeWidth="1.5" strokeDasharray="2 4" />
      <g className={spinning ? 'hub-orbit' : undefined} style={{ transformOrigin: '24px 24px' }}>
        <circle cx="24" cy="7" r="4.5" fill="rgb(var(--chart-2))" />
        <circle cx="38.7" cy="32.5" r="4.5" fill="rgb(var(--chart-4))" />
        <circle cx="9.3" cy="32.5" r="4.5" fill="rgb(var(--chart-3))" />
      </g>
      <circle cx="24" cy="24" r="7" fill="rgb(var(--brand-solid))" className={spinning ? 'hub-pulse' : undefined} style={{ transformOrigin: '24px 24px' }} />
    </svg>
  );
}

export function Loading({ label, inline = false }: { label?: string; inline?: boolean }) {
  const [i, setI] = useState(() => Math.floor(Math.random() * LINES.length));
  const [showText, setShowText] = useState(false);
  useEffect(() => {
    const t = window.setTimeout(() => setShowText(true), 500);
    const r = window.setInterval(() => setI((x) => (x + 1) % LINES.length), 1800);
    return () => { window.clearTimeout(t); window.clearInterval(r); };
  }, []);

  if (inline) {
    return (
      <span className="inline-flex items-center gap-2 text-sm text-fg-faint" role="status">
        <HubMark size={18} />
        <span>{label ? `Loading ${label}` : 'Loading'}</span>
      </span>
    );
  }
  const line = label && i % 3 === 0 ? `Loading ${label}` : LINES[i];
  return (
    <div className="flex flex-col items-center justify-center gap-3 py-12" role="status" aria-label={label ? `Loading ${label}` : 'Loading'}>
      <HubMark />
      <p
        key={line}
        className={`text-sm text-fg-muted transition-opacity duration-base ${showText ? 'opacity-100 hub-line' : 'opacity-0'}`}
      >
        {line}…
      </p>
    </div>
  );
}
